<script setup lang="ts">
	import { Ref, ref } from 'vue'
	import { Carousel, Navigation, Slide } from 'vue3-carousel'
	import { Collapse } from 'vue-collapsed'

	import TripadvisorPanel from '@/modules/hotels/components/TripadvisorPanel.vue'
	import { CarouselModels } from '@/modules/hotels/interfaces/carouselModels'
	import { Hotel, Item } from '@/modules/hotels/interfaces/hotelDetailsResponse'
	import { HotelImages } from '@/modules/hotels/interfaces/hotelImages'

	import { HotelText } from '../interfaces/hotelText'

	interface Props {
		hotel: Hotel
		hotelImages: HotelImages
		hotelTexts: HotelText[]
		haveAttractions: boolean
		showMoreImages: boolean
		carouselModels: CarouselModels
		carouselImages: string[]
	}

	interface Emits {
		(event: 'toggleMoreImages'): () => void
		(event: 'showMultiple', i: number, images: Item[]): void
		(event: 'update:carouselModels', value: CarouselModels): void
	}

	const props = defineProps<Props>()
	const emits = defineEmits<Emits>()
	const photos = ref(null)
	const details = ref(null)
	const tripadvisor = ref<InstanceType<typeof TripadvisorPanel> | null>(null)

	const updateCarouselModelsHandler = (code: number, value: number) => {
		const newCarouselModels = { ...props.carouselModels }
		newCarouselModels[code].value = value
		emits('update:carouselModels', newCarouselModels)
	}

	interface Expose {
		photos: Ref
		details: Ref
		tripadvisor: Ref
	}

	defineExpose<Expose>({ photos, details, tripadvisor })
</script>

<template>
	<!-- Photos - Premium Gallery -->
	<div
		v-if="hotelImages.availableCategories.length > 0"
		ref="photos"
		class="w-full rounded-xl md:rounded-2xl border border-gray-200 md:border-2 bg-white p-3 sm:p-4 md:p-6 shadow-md md:shadow-lg"
	>
		<div class="grid gap-3 sm:gap-4 md:gap-6">
			<div class="flex items-center gap-2 sm:gap-3">
				<span
					class="material-symbols-outlined text-gray-700 text-xl sm:text-2xl"
					>photo_library</span
				>
				<h2 class="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900">
					Galería del Hotel
				</h2>
			</div>

			<div
				class="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"
			></div>

			<!-- First two categories -->
			<div class="grid grid-cols-1 gap-3 sm:gap-4 md:gap-6 md:grid-cols-2">
				<div
					v-for="category in hotelImages.remainingCategories.slice(0, 2)"
					:key="category.code"
					class="flex flex-col gap-2 sm:gap-3"
				>
					<h3
						class="text-sm sm:text-base font-semibold text-gray-700 flex items-center gap-1.5 sm:gap-2"
					>
						<span class="w-1 h-5 bg-blue-600 rounded-full"></span>
						{{ category.description }}
					</h3>

					<Carousel
						v-if="category.images.length > 1 && carouselModels[category.code]"
						:model-value="carouselModels[category.code].value"
						:items-to-show="1"
						:transition="400"
						:mouse-drag="false"
						@update:model-value="
							updateCarouselModelsHandler(category.code, $event)
						"
					>
						<Slide
							v-for="(image, a) in category.images"
							:key="image.url"
							class="cursor-pointer group"
						>
							<div
								class="carousel__item relative overflow-hidden rounded-xl shadow-md"
								@click="$emit('showMultiple', a, category.images)"
							>
								<img
									class="aspect-[6/4] h-full w-full object-cover transform transition-transform duration-500 group-hover:scale-110"
									:src="image.url"
									alt=""
								/>
								<div
									class="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
								></div>
							</div>
						</Slide>
						<template #addons>
							<Navigation />
						</template>
					</Carousel>

					<div
						v-else
						class="carousel__item cursor-pointer group relative overflow-hidden rounded-xl shadow-md"
						@click="$emit('showMultiple', 0, category.images)"
					>
						<img
							class="aspect-[5/3] h-full w-full object-cover transform transition-transform duration-500 group-hover:scale-105"
							:src="category.images[0].url"
							alt=""
						/>
						<div
							class="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
						></div>
					</div>
				</div>
			</div>

			<!-- Remaining categories - Premium Style -->
			<Collapse
				:when="showMoreImages"
				class="v-collapse"
			>
				<div
					v-if="showMoreImages"
					class="grid grid-cols-1 gap-3 sm:gap-4 md:gap-6 md:grid-cols-2 pt-4 sm:pt-5 md:pt-3"
				>
					<div
						v-for="category in hotelImages.remainingCategories.slice(2)"
						:key="category.code"
						class="flex flex-col gap-3 sm:gap-4"
					>
						<h3
							class="text-sm sm:text-base font-semibold text-gray-700 flex items-center gap-1.5 sm:gap-2"
						>
							<span class="w-1 h-5 bg-blue-600 rounded-full"></span>
							{{ category.description }}
						</h3>

						<Carousel
							v-if="category.images.length > 1"
							:model-value="carouselModels[category.code].value"
							:items-to-show="1"
							:transition="400"
							:mouse-drag="false"
							@update:model-value="
								updateCarouselModelsHandler(category.code, $event)
							"
						>
							<Slide
								v-for="(image, a) in category.images"
								:key="image.url"
								class="cursor-pointer group"
							>
								<div
									class="carousel__item relative overflow-hidden rounded-xl shadow-md"
									@click="$emit('showMultiple', a, category.images)"
								>
									<img
										loading="lazy"
										class="aspect-[6/4] h-full w-full object-cover transform transition-transform duration-500 group-hover:scale-110"
										:src="image.url"
										alt=""
									/>
									<div
										class="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
									></div>
								</div>
							</Slide>
							<template #addons>
								<Navigation />
							</template>
						</Carousel>

						<div
							v-else
							class="carousel__item cursor-pointer group relative overflow-hidden rounded-xl shadow-md"
							@click="$emit('showMultiple', 0, category.images)"
						>
							<img
								loading="lazy"
								class="aspect-[5/3] h-full w-full object-cover transform transition-transform duration-500 group-hover:scale-105"
								:src="category.images[0].url"
								alt=""
							/>
							<div
								class="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
							></div>
						</div>
					</div>
				</div>
			</Collapse>

			<!-- Expand/Hide categories -->
			<button
				v-if="hotelImages.remainingCategories.slice(2).length > 0"
				class="mx-auto flex items-center gap-1.5 sm:gap-2 px-4 py-2.5 sm:px-6 sm:py-3 rounded-lg sm:rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 text-sm sm:text-base font-semibold hover:from-blue-100 hover:to-indigo-100 transition-all duration-300 hover:shadow-md group border border-blue-200 active:scale-95 min-h-[44px] mt-6"
				@click="$emit('toggleMoreImages')"
			>
				<span class="truncate">{{
					showMoreImages ? 'Ver menos fotos' : (
						`Ver ${hotelImages.remainingCategories.slice(2).length} categorías más`
					)
				}}</span>
				<span
					:class="[
						'material-symbols-outlined transition-transform duration-300 group-hover:translate-y-0.5',
						showMoreImages && 'rotate-180',
					]"
				>
					expand_more
				</span>
			</button>
		</div>
	</div>

	<!-- Provider details and optional Tripadvisor content share the Details anchor. -->
	<div
		ref="details"
		class="flex min-w-0 flex-col gap-4"
	>
		<div
			v-if="hotelTexts.length"
			class="w-full rounded-lg md:rounded-xl border bg-white p-3 sm:p-4 md:p-5 shadow-sm md:shadow-md"
		>
			<div class="grid gap-3 sm:gap-4">
				<span
					class="border-b-2 border-gray-300 pb-2 text-base sm:text-lg font-medium leading-6 text-gray-900"
					>Detalles</span
				>

				<div class="flex flex-col gap-3 sm:gap-4">
					<article
						v-for="text in hotelTexts"
						:key="text.title"
						class="grid gap-1.5 sm:gap-2"
					>
						<div class="flex items-center gap-1.5 sm:gap-2">
							<!-- <span class="material-symbols-outlined">{{ text.icon }}</span>cr -->
							<p class="text-sm sm:text-base font-medium">{{ text.title }}</p>
						</div>
						<div class="flex justify-start gap-2">
							<p
								:class="[
									'text-xs sm:text-sm md:text-base font-light leading-relaxed',
									text.description.length > 150 && 'text-justify',
								]"
							>
								{{ text.description }}
							</p>
						</div>
					</article>
				</div>
			</div>
		</div>
		<TripadvisorPanel
			v-if="hotel.tripadvisorHotelId"
			ref="tripadvisor"
			:key="hotel.tripadvisorHotelId"
			:hotel-id="hotel.tripadvisorHotelId"
		/>
	</div>
</template>

<style scoped></style>
